// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DollarSign,
  TrendingUp,
  Calendar,
  Download,
  Loader2,
  CreditCard,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';

interface GraphicsEarning {
  id: string;
  amount: number;
  commission_rate: number;
  order_id: string;
  status: 'pending' | 'paid' | 'cancelled';
  earned_at: string;
  paid_at?: string;
}

interface GraphicsPayout {
  id: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  payout_method: string;
  created_at: string;
  processed_at?: string;
  stripe_transfer_id?: string;
}

const VendorFinance: React.FC = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState<GraphicsEarning[]>([]);
  const [payouts, setPayouts] = useState<GraphicsPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingEarnings: 0,
    paidEarnings: 0,
    availableBalance: 0,
  });

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);

      // First get the vendor profile
      const { data: vendorProfile, error: vendorError } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (vendorError) throw vendorError;

      // Fetch earnings
      const { data: earningsData, error: earningsError } = await supabase
        .from('graphics_vendor_earnings')
        .select('*')
        .eq('vendor_id', vendorProfile.id)
        .order('earned_at', { ascending: false });

      if (earningsError) throw earningsError;

      // Fetch payouts
      const { data: payoutsData, error: payoutsError } = await supabase
        .from('graphics_vendor_payouts')
        .select('*')
        .eq('vendor_id', vendorProfile.id)
        .order('created_at', { ascending: false });

      if (payoutsError) throw payoutsError;

      setEarnings(earningsData || []);
      setPayouts(payoutsData || []);

      // Calculate stats
      const totalEarnings = earningsData?.reduce((sum, earning) => sum + earning.amount, 0) || 0;
      const pendingEarnings = earningsData?.filter(e => e.status === 'pending').reduce((sum, earning) => sum + earning.amount, 0) || 0;
      const paidEarnings = earningsData?.filter(e => e.status === 'paid').reduce((sum, earning) => sum + earning.amount, 0) || 0;
      const totalPayouts = payoutsData?.filter(p => p.status === 'completed').reduce((sum, payout) => sum + payout.amount, 0) || 0;
      const availableBalance = paidEarnings - totalPayouts;

      setStats({
        totalEarnings,
        pendingEarnings,
        paidEarnings,
        availableBalance,
      });

    } catch (error) {
      console.error('Error fetching finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const requestPayout = async () => {
    setShowPayoutModal(true);
  };

  const formatPrice = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEarningStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, label: 'Pending' },
      paid: { variant: 'default' as const, label: 'Paid' },
      cancelled: { variant: 'destructive' as const, label: 'Cancelled' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const getPayoutStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, label: 'Pending' },
      processing: { variant: 'default' as const, label: 'Processing' },
      completed: { variant: 'default' as const, label: 'Completed' },
      failed: { variant: 'destructive' as const, label: 'Failed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading finance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Finance</h1>
          <p className="text-muted-foreground">
            Track your earnings, manage payouts, and view financial reports.
          </p>
        </div>
        <Button 
          onClick={requestPayout}
          disabled={stats.availableBalance <= 0}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Request Payout
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.totalEarnings)}</div>
            <p className="text-xs text-muted-foreground">
              All time earnings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Earnings</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatPrice(stats.pendingEarnings)}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting payment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Earnings</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatPrice(stats.paidEarnings)}</div>
            <p className="text-xs text-muted-foreground">
              Released for withdrawal
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatPrice(stats.availableBalance)}</div>
            <p className="text-xs text-muted-foreground">
              Ready to withdraw
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Earnings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Earnings</CardTitle>
          <CardDescription>
            Your earnings from completed graphics design orders.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {earnings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Amount</TableHead>
                  <TableHead>Commission Rate</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Earned Date</TableHead>
                  <TableHead>Paid Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {earnings.map((earning) => (
                  <TableRow key={earning.id}>
                    <TableCell className="font-medium">
                      {formatPrice(earning.amount)}
                    </TableCell>
                    <TableCell>{earning.commission_rate}%</TableCell>
                    <TableCell className="font-mono text-xs">{earning.order_id?.substring(0, 8)}...</TableCell>
                    <TableCell>
                      {getEarningStatusBadge(earning.status)}
                    </TableCell>
                    <TableCell>{formatDate(earning.earned_at)}</TableCell>
                    <TableCell>
                      {earning.paid_at ? formatDate(earning.paid_at) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Earnings Yet</h3>
              <p className="text-muted-foreground mb-4">
                Complete graphics orders to start earning commissions.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
          <CardDescription>
            Your withdrawal requests and payout history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {payouts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead>Transfer ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell className="font-medium">
                      {formatPrice(payout.amount)}
                    </TableCell>
                    <TableCell className="capitalize">{payout.payout_method}</TableCell>
                    <TableCell>
                      {getPayoutStatusBadge(payout.status)}
                    </TableCell>
                    <TableCell>{formatDate(payout.created_at)}</TableCell>
                    <TableCell>
                      {payout.processed_at ? formatDate(payout.processed_at) : '-'}
                    </TableCell>
                    <TableCell>
                      {payout.stripe_transfer_id || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Download className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Payouts Yet</h3>
              <p className="text-muted-foreground mb-4">
                Request your first payout when you have available earnings.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default VendorFinance;